import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { usePagination } from '../../hooks/usePagination'
import PaginationControls from '../../components/PaginationControls'
import { getCachedCount, setCachedCount, invalidateCachedCount } from '../../lib/countCache'

type PortType = 'AIRPORT' | 'SEAPORT' | 'INLAND'

interface Port {
  id?: string
  code: string
  name: string
  type: PortType
  country: string
  is_active: boolean
  un_locode: string | null
}

const PORT_TYPES: PortType[] = ['AIRPORT', 'SEAPORT', 'INLAND']

const TYPE_COLORS: Record<PortType, string> = {
  AIRPORT: 'bg-sky-100 text-sky-800',
  SEAPORT: 'bg-teal-100 text-teal-800',
  INLAND: 'bg-lime-100 text-lime-800',
}

const empty: Port = { code: '', name: '', type: 'AIRPORT', country: '', is_active: true, un_locode: null }

// Z23: BoxTop needs `code` (its own Code column, e.g. NGB) and un_locode (the 5-char UN/LOCODE,
// e.g. CNNGB) matched by type — AIRPORT codes are the 3-char IATA prefix (LOCODE optional,
// airlines route on IATA); SEAPORT rows need both, LOCODE always exactly 5 chars.
function codeMaxLength(type: PortType): number {
  return type === 'AIRPORT' ? 3 : type === 'SEAPORT' ? 5 : 10
}
const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  )
}

function Modal({ title, onClose, onSubmit, saving, error, children }: {
  title: string; onClose: () => void; onSubmit: (e: React.FormEvent) => void
  saving: boolean; error: string | null; children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}
          {children}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function Ports() {
  const { userRoles } = useAuth()
  // ports RLS only allows role='ADMIN' to insert/update/delete (20260507_enable_rls_all_tables.sql)
  // — OPERATIONS could see these Edit/Add buttons but any write would be rejected by the DB.
  const canEdit = userRoles.includes('ADMIN')

  const [rows, setRows] = useState<Port[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState<PortType | ''>('')
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('active')
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState<Port>(empty)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [total, setTotal] = useState<number | null>(null)
  const { page, setPage, pageSize, setPageSize, range } = usePagination('ports-list')

  // Z56: 41k+ rows in this table (by far the largest list page in the app) -- was previously
  // fetching every matching row unfiltered on every keystroke/filter change.
  const load = useCallback(async () => {
    setLoading(true)
    // Z35.1: total count only needs a fresh count(*) once per 30s per distinct filter set.
    const countCacheKey = `ports-list:${JSON.stringify({ search, filterType, filterActive })}`
    const cachedCount = getCachedCount(countCacheKey)
    let q = supabase.from('ports').select('*', cachedCount === undefined ? { count: 'exact' } : undefined).order('code')
    if (search) q = q.or(`name.ilike.%${search}%,code.ilike.%${search}%,un_locode.ilike.%${search}%,country.ilike.%${search}%`)
    if (filterType) q = q.eq('type', filterType)
    if (filterActive === 'active') q = q.eq('is_active', true)
    if (filterActive === 'inactive') q = q.eq('is_active', false)
    if (range) q = q.range(range[0], range[1])
    const { data, count } = await q
    setRows(data || [])
    if (cachedCount !== undefined) {
      setTotal(cachedCount)
    } else {
      setTotal(count ?? 0)
      setCachedCount(countCacheKey, count ?? 0)
    }
    setLoading(false)
  }, [search, filterType, filterActive, range])

  useEffect(() => { setPage(0) }, [search, filterType, filterActive, setPage])
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t) }, [load])

  const openAdd = () => { setForm(empty); setError(null); setModal(true) }
  const openEdit = (r: Port) => { setForm({ ...r }); setError(null); setModal(true) }
  const set = (k: keyof Port, v: unknown) => setForm(p => ({ ...p, [k]: v }))

  // Z26: a 5-char UN/LOCODE starts with the ISO2 country, so only the country is prefilled from it.
  // The BoxTop Code is NEVER derived from the LOCODE any more: BoxTop's 3-char code is its own coding
  // (e.g. BHX for GBBHM, YVR for CAVAN), so LOCODE[2:] is wrong for many ports (checked against BoxTop's
  // own linked ports 2026-10-22). Staff enter it, copied from BoxTop's Port dropdown.
  const deriveFromLocode = (locode: string, current: Port): Partial<Port> => {
    if (current.type !== 'SEAPORT') return {}
    const clean = locode.trim().toUpperCase()
    if (clean.length !== 5) return {}
    const updates: Partial<Port> = {}
    if (!current.country?.trim()) updates.country = clean.slice(0, 2)
    return updates
  }

  const handleLocodeChange = (raw: string) => {
    const clean = raw.toUpperCase()
    setForm(prev => ({ ...prev, un_locode: clean, ...(clean.length === 5 ? deriveFromLocode(clean, prev) : {}) }))
  }

  const handleLocodeBlur = () => {
    setForm(prev => {
      const derived = deriveFromLocode(prev.un_locode || '', prev)
      return Object.keys(derived).length > 0 ? { ...prev, ...derived } : prev
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { setError('Name is required.'); return }
    const locode = form.un_locode?.trim().toUpperCase() || ''
    if (locode && locode.length !== 5) { setError('Location Code (UN/LOCODE) must be exactly 5 characters.'); return }
    if (form.type === 'SEAPORT' && !locode) { setError('Location Code (UN/LOCODE) is required for seaports.'); return }

    // The Code column is BoxTop's own 3-char port code (what its Port dropdown shows in front of the name),
    // entered by staff -- never derived from the LOCODE. For a SEAPORT it may be left blank when it is not
    // known yet: the row then keeps the LOCODE as a placeholder code (the legacy shape of ~32,000 rows) and
    // the port cannot be synced to BoxTop until a real 3-char code is filled in.
    let code = form.code.trim().toUpperCase()
    if (form.type === 'SEAPORT') {
      if (!code) code = locode
      else if (code !== locode && !/^[A-Z0-9]{3}$/.test(code)) { setError('Code (BoxTop) must be exactly 3 characters, as shown in front of the port name in BoxTop\'s Port dropdown.'); return }
    } else {
      if (!code) { setError('Code is required.'); return }
      if (form.type === 'AIRPORT' && !/^[A-Z]{1,3}$/.test(code)) { setError('IATA code must be 1-3 uppercase letters.'); return }
      const maxLen = codeMaxLength(form.type)
      if (code.length > maxLen) { setError(`Code must be ${maxLen} characters or fewer for ${form.type}.`); return }
    }
    setSaving(true); setError(null)
    try {
      const payload = { code, name: form.name, type: form.type, country: form.country, is_active: form.is_active, un_locode: locode || null }
      if (form.id) {
        const { error: err } = await supabase.from('ports').update(payload).eq('id', form.id)
        if (err) throw err
      } else {
        const { error: err } = await supabase.from('ports').insert(payload)
        if (err) throw err
      }
      invalidateCachedCount('ports-list')
      setModal(false); load()
    } catch (err: any) { setError(err?.message || err?.error_description || String(err) || 'Unknown error') }
    finally { setSaving(false) }
  }

  const toggleActive = async (r: Port) => {
    await supabase.from('ports').update({ is_active: !r.is_active }).eq('id', r.id!)
    invalidateCachedCount('ports-list') // is_active is a filter -- this changes the counted set
    load()
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/data" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">← Data Management</Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mt-1">Ports & Airports</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">Airports, seaports and inland terminals used on jobs</p>
        </div>

        <div className="flex flex-wrap gap-3 mb-4 items-end">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search code, name or country…"
            className="flex-1 min-w-[200px] border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
          <select value={filterType} onChange={e => setFilterType(e.target.value as PortType | '')}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
            <option value="">All Types</option>
            {PORT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterActive} onChange={e => setFilterActive(e.target.value as typeof filterActive)}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
            <option value="all">All</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
          {canEdit && (
            <button onClick={openAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              + Add Port
            </button>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          {/* Z35.1: controls at both top and bottom of the list */}
          <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={total} />
          {loading ? (
            <div className="py-12 text-center text-gray-400">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  {['Identifier', 'Name', 'Type', 'Country', 'Status', ...(canEdit ? ['Actions'] : [])].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map(r => {
                  // Z33: each type has exactly one authoritative identifier -- SEAPORT's is the
                  // UN/LOCODE, AIRPORT's (and INLAND's) is the internal/BoxTop Code -- showing
                  // both, or the wrong one, is ambiguous now that Z27/Z29 made `code`/`un_locode`
                  // unique only per type, not globally.
                  const identifier = r.type === 'SEAPORT' ? r.un_locode : r.code
                  const missingLabel = r.type === 'SEAPORT' ? 'Missing LOCODE - click Edit to update' : 'Missing code - click Edit to update'
                  return (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-mono font-bold text-gray-900 dark:text-white">
                      {identifier || <span className="text-gray-400">—</span>}
                      {!identifier && <span title={missingLabel} className="text-amber-500 ml-1">▲</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{r.name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${TYPE_COLORS[r.type]}`}>
                        {r.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{r.country || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3 space-x-3">
                        <button onClick={() => openEdit(r)} className="text-blue-600 hover:underline text-xs font-medium">Edit</button>
                        <button onClick={() => toggleActive(r)} className={`text-xs font-medium ${r.is_active ? 'text-red-500 hover:underline' : 'text-green-600 hover:underline'}`}>
                          {r.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    )}
                  </tr>
                  )
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={canEdit ? 6 : 5} className="px-4 py-8 text-center text-gray-400">No ports found</td></tr>
                )}
              </tbody>
            </table>
          )}
          <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={total} />
        </div>
      </div>

      {modal && (
        <Modal title={form.id ? 'Edit Port' : 'Add Port'} onClose={() => setModal(false)} onSubmit={handleSubmit} saving={saving} error={error}>
          <div className="grid grid-cols-2 gap-4">
            <Field label={form.type === 'AIRPORT' ? 'IATA Code' : 'Code (BoxTop, 3 chars)'} required={form.type !== 'SEAPORT'}>
              <input className={inputCls} value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} placeholder={form.type === 'AIRPORT' ? 'e.g. LHR' : form.type === 'SEAPORT' ? 'e.g. NGB' : 'e.g. code'} maxLength={codeMaxLength(form.type)} />
              {form.type === 'SEAPORT' && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Copy it from BoxTop's Port dropdown (the code in front of the name). Leave blank if unknown — the port then can't be synced to BoxTop.</p>}
            </Field>
            <Field label="Type" required>
              <select className={inputCls} value={form.type} onChange={e => set('type', e.target.value as PortType)}>
                {PORT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          {form.type !== 'AIRPORT' && (
            <Field label="Location Code (UN/LOCODE)" required={form.type === 'SEAPORT'}>
              <input
                className={inputCls}
                value={form.un_locode || ''}
                onChange={e => handleLocodeChange(e.target.value)}
                onBlur={handleLocodeBlur}
                placeholder="e.g. CNNGB"
                maxLength={5}
              />
            </Field>
          )}
          <Field label="Name" required>
            <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} placeholder="London Heathrow Airport" />
          </Field>
          <Field label="Country">
            <input className={inputCls} value={form.country} onChange={e => set('country', e.target.value)} placeholder="United Kingdom" />
          </Field>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
          </label>
        </Modal>
      )}
    </div>
  )
}
